import { useState, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const WaitlistForm = forwardRef<HTMLInputElement>((props, ref) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Starting waitlist submission for:", email);
      const normalizedEmail = email.toLowerCase().trim();

      // Insert new subscriber directly (RLS prevents anonymous SELECTs)
      console.log("Inserting new subscriber into database (no pre-check)");
      const { error: insertError } = await supabase
        .from('subscribers')
        .insert([{ email: normalizedEmail, subscribed: true }]);
        
      if (insertError) {
        console.error("Error inserting subscriber:", insertError);
        const message = String(insertError.message || "").toLowerCase();
        const details = String((insertError as any).details || "").toLowerCase();
        const isDuplicate = message.includes("duplicate") || message.includes("unique")
          || details.includes("duplicate") || details.includes("unique")
          || (insertError as any).code === "23505";
        
        if (isDuplicate) {
          toast({
            title: "Already subscribed",
            description: "This email is already on our waitlist.",
          });
          
          setIsSubmitting(false);
          navigate("/thank-you");
          return;
        }
        
        throw insertError;
      }
      
      console.log("Successfully inserted subscriber");
      
      // Call welcome email function with proper authentication via Supabase client
      console.log("Calling welcome email function via supabase.functions.invoke for:", normalizedEmail);
      const { data: fnData, error: fnError } = await supabase.functions.invoke('send-welcome-email', {
        body: { email: normalizedEmail },
      });
      
      if (fnError) {
        console.error("Welcome email sending failed:", fnError);
        toast({
          title: "Database updated",
          description: "You've been added to our database, but there may be an issue with our email service.",
          variant: "default",
        });
      } else {
        console.log("Welcome email sent successfully", fnData);
        toast({
          title: "Success!",
          description: "You've been added to our waitlist.",
        });
      }
      
      setEmail("");
      navigate("/thank-you");
    } catch (error) {
      console.error("Error in waitlist submission process:", error);
      
      toast({
        title: "Something went wrong",
        description: "We couldn't add you to the waitlist. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex flex-col sm:flex-row gap-2 bg-white rounded-lg overflow-hidden">
        <Input
          ref={ref}
          type="email"
          placeholder="Yes,show me how to earn yield!"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full h-12 border-0 text-black placeholder:text-gray-600 focus-visible:ring-0"
          required
          aria-label="Email Address"
        />
        <Button 
          type="submit" 
          className="h-12 px-6 bg-[#FF3366] hover:bg-[#E62E5C] text-white font-bold"
          disabled={isSubmitting}
        >
          {isSubmitting ? "JOINING..." : "SUBSCRIBE"}
        </Button>
      </div>
    </form>
  );
});

WaitlistForm.displayName = "WaitlistForm";

export default WaitlistForm;
